import clsx from 'clsx';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { Spinner } from '../spinner/Spinner';

import styles from './Button.module.css';

export type ButtonVariant =
  'default' | 'primary' | 'ai' | 'success' | 'danger' | 'warn' | 'info' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: 'sm' | 'md';
  readonly block?: boolean;
  readonly loading?: boolean;
  readonly icon?: ReactNode;
}

/**
 * The single button primitive. Feature code never styles a raw `<button>`,
 * which is what keeps the eight-colour palette from drifting.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    block,
    loading,
    icon,
    disabled,
    children,
    className,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled ?? loading}
      aria-busy={loading}
      className={clsx(
        styles.btn,
        variant !== 'default' && styles[variant],
        size === 'sm' && styles.sm,
        block && styles.block,
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={12} /> : icon}
      {children}
    </button>
  );
});
